import { useEffect, useState } from 'react';
import { Button, InlineNotification, Layer, TextArea } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  activeTcWorkspaceAtom,
  tcDetailsRightPanelAtom,
  type TcVerificationDecisionKind,
} from './atoms';
import {
  useSaveWorkspaceTask,
  parseTaskError,
  OVERALL_TYPE_CURVE_KEY,
} from './tasks';
import style from './tc-verification-decision-panel.module.scss';

export interface TcVerificationDecisionPanelProps {
  kind: TcVerificationDecisionKind;
}

export const TcVerificationDecisionPanel = ({
  kind,
}: TcVerificationDecisionPanelProps) => {
  const setRightPanel = useSetAtom(tcDetailsRightPanelAtom);
  const workspace = useAtomValue(activeTcWorkspaceAtom);
  const [reason, setReason] = useState('');
  const [reasonBlurred, setReasonBlurred] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Save patches the overall Type Curve verification task (path `type_curve`);
  // its onSuccess re-mirrors the tasks atom, so the header buttons update.
  const saveTask = useSaveWorkspaceTask();

  useEffect(() => {
    if (!workspace) {
      setRightPanel({ type: 'dca', open: false });
    }
  }, [workspace, setRightPanel]);

  useEffect(() => {
    setReason('');
    setReasonBlurred(false);
    setSaveError(null);
  }, [kind]);

  const close = () => setRightPanel({ type: 'dca', open: false });

  if (!workspace) {
    return null;
  }
  const isCorrect = kind === 'correct';

  const title = isCorrect ? 'Mark as Correct' : 'Mark as Incorrect';
  const helperSubtitle = isCorrect
    ? 'Why are you marking this Type Curve as correct?'
    : 'Why are you marking this Type Curve as incorrect?';
  const placeholder = isCorrect
    ? 'Explain why this Type Curve is correct.'
    : 'Explain why this Type Curve is incorrect.';

  const reasonTrimmed = reason.trim();
  const reasonInvalid = reasonBlurred && reasonTrimmed.length === 0;
  const canSubmit = reasonTrimmed.length > 0 && !saveTask.isPending;

  const handleSave = () => {
    if (!reasonTrimmed) {
      setReasonBlurred(true);
      return;
    }

    setSaveError(null);

    saveTask.mutate(
      {
        path: OVERALL_TYPE_CURVE_KEY,
        correctnessState: isCorrect,
        reason: reasonTrimmed,
      },
      {
        onSuccess: () => close(),
        onError: async (error) => {
          const message = await parseTaskError(error);
          setSaveError(message ?? 'Unable to save. Please try again.');
        },
      }
    );
  };

  return (
    <div className={style.root}>
      <div className={style.panelHeader}>
        <div className={style.headerCopy}>
          <h2 className={style.title}>{title}</h2>
          <p className={style.subtitle}>{helperSubtitle}</p>
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={close}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </div>
      <div className={style.panelBody}>
        {saveError ? (
          <InlineNotification
            kind="error"
            title="Save failed"
            subtitle={saveError}
            lowContrast
            hideCloseButton
            className={style.errorNote}
          />
        ) : null}
        <div className={style.fieldWrap}>
          <Layer>
            <TextArea
              id="tc-verification-decision-reason"
              labelText="Reason"
              placeholder={placeholder}
              value={reason}
              invalid={reasonInvalid}
              invalidText={reasonInvalid ? 'A reason is required.' : undefined}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setReasonBlurred(true)}
              className={style.field}
              disabled={saveTask.isPending}
            />
          </Layer>
        </div>
      </div>
      <div className={style.footer}>
        <Button
          kind="secondary"
          size="lg"
          onClick={close}
          disabled={saveTask.isPending}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          size="lg"
          onClick={handleSave}
          disabled={!canSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
